import { useMemo } from 'react';
import classNames from 'classnames';
import { useAppContext } from '$util/AppContext';
import type { VrmLookupSectionProps } from '$components/VrmLookupSection';
import { MotServiceBundleBanner, PartnerBanner } from '$components/VrmLookupSection';
import { HeroSection } from '$src-components/organisms/HeroSection';
import { getHeroSectionCmsData } from '$src-components/organisms/HeroSection/cms';
import type {
  HeroSectionConfiguration,
  HeroSectionTranslatedEntities,
} from '@/components/organisms/HeroSection/schema';
import styles from './index.module.scss';

export interface HeroSectionStaticProps extends VrmLookupSectionProps {
  /**
   * Override the CMS translated entities from the static page.
   */
  readonly overrideTranslatedEntries?: Partial<HeroSectionTranslatedEntities>;
  readonly configuration?: HeroSectionConfiguration;
  readonly reviewsPositionCenter?: true;
}

/**
 * Made for the reskin to hardcode the `HeroSection` ContentComponent on old UK pages
 * that haven't yet been migrated to Contentful.
 * It also brings some additional logic from `components/VrmLookupSection`.
 *
 * @todo remove after pages have been migrated
 */
export function HeroSectionStatic({
  overrideTranslatedEntries,
  configuration,
  partnerData,
  reviewsPositionCenter,
  className,
}: HeroSectionStaticProps) {
  const {
    location: { pathname },
    pageTypeFromPath,
    cmsContent: { dictionary },
  } = useAppContext();

  const translatedEntities = useMemo(() => {
    const { translatedEntities: cmsTranslatedEntities } = getHeroSectionCmsData(dictionary);
    return Object.assign(cmsTranslatedEntities, overrideTranslatedEntries);
  }, [dictionary, overrideTranslatedEntries]);

  return (
    <>
      {pageTypeFromPath === 'partner' && <PartnerBanner pathname={pathname} partnerData={partnerData} />}

      {pathname.includes('mot-and-service') && <MotServiceBundleBanner />}

      <HeroSection
        id={HeroSectionStatic.displayName}
        className={classNames(
          styles.HeroSection,
          reviewsPositionCenter && styles.reviewsPositionCenter,
          className
        )}
        translatedEntities={translatedEntities}
        configuration={configuration}
      />
    </>
  );
}

HeroSectionStatic.displayName = 'HeroSectionStatic';
